import { FC, MouseEvent, ReactNode, useCallback, useMemo } from 'react';
import { Box, Stack, SvgIcon, Typography } from '@mui/material';

import DotIcon from '@mopla/ui/icons/DotOutlinedIcon.svg';
import LocationPointV2Icon from '@mopla/ui/icons/LocationPointV2.svg';
import LocationStopV2Icon from '@mopla/ui/icons/LocationStopV2.svg';
import { formatTime, openMapsApp } from '@mopla/utils';

import {
	LocationAddress,
	RouteStopContent,
	TimelineConnector,
	TimelineIconContainer,
} from './styled';
import { IRouteStop } from './types';
import { ERouteStopVariant } from './utils';

interface IRouteStopProps extends IRouteStop {
	variant: ERouteStopVariant;
	/** Content after the address (SegmentCard, WalkIndicator) */
	children?: ReactNode;
	/** Show dashed connector line (for walk segments) */
	dashedConnector?: boolean;
	/** Marks the times as estimates, e.g. during a delay of unknown length. */
	isApproximate?: boolean;
}

/**
 * Single route stop row.
 * Left: dot icon with timeline connector (except last)
 * Right: time, address, and optional detail content
 */
export const RouteStop: FC<IRouteStopProps> = ({
	variant = ERouteStopVariant.INTERMEDIATE,
	time,
	effectiveTime,
	address,
	lat,
	lng,
	children,
	dashedConnector = false,
	isApproximate = false,
	addressTestId,
}) => {
	const isLast = variant === ERouteStopVariant.END;

	const formattedEffectiveTime = useMemo(
		() => (effectiveTime ? formatTime(effectiveTime) : null),
		[effectiveTime]
	);

	const formattedTime = useMemo(() => formatTime(time), [time]);

	/** Stops the click from also reaching a clickable card around the route. */
	const handleAddressClick = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			openMapsApp({ lat, lng });
		},
		[lat, lng]
	);

	const getIcon = () => {
		switch (variant) {
			case ERouteStopVariant.START:
				return DotIcon;
			case ERouteStopVariant.END:
				return LocationPointV2Icon;
			default:
				return LocationStopV2Icon;
		}
	};

	return (
		<Stack direction="row" gap={1.5}>
			<TimelineIconContainer isStart={variant === ERouteStopVariant.START}>
				<SvgIcon component={getIcon()} color="primary" fontSize="xs" />
				{!isLast && <TimelineConnector dashed={dashedConnector} />}
			</TimelineIconContainer>

			<RouteStopContent isLast={isLast}>
				<Stack>
					<Stack direction="row" gap={0.5} alignItems="center">
						<Typography
							variant="b2"
							fontWeight={formattedEffectiveTime ? 400 : 600}
							color={formattedEffectiveTime ? 'solutions.600' : 'primary'}
							sx={
								formattedEffectiveTime
									? { textDecoration: 'line-through' }
									: undefined
							}
						>
							{/** A re-predicted time is exact, so only a lone scheduled
							 *   time can be an estimate. */}
							{!formattedEffectiveTime && isApproximate && '~'}
							{formattedTime}
						</Typography>
						{formattedEffectiveTime && (
							<Typography variant="b2" fontWeight={600} color="primary">
								{formattedEffectiveTime}
							</Typography>
						)}
					</Stack>
					{/** Block wrapper takes the flex-item role, so the address stays
					 *   inline-block and its click area matches the text width. */}
					<div>
						<LocationAddress
							variant="b2"
							onClick={handleAddressClick}
							data-testid={addressTestId}
						>
							{address}
						</LocationAddress>
					</div>
				</Stack>

				{children}
			</RouteStopContent>
		</Stack>
	);
};
